import React, { useCallback, useMemo, useState } from "react";
import { useAtom } from "jotai";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Divider, Grid, Header, Icon, Loader, Message, Table } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { countriesAtom, currenciesAtom, showVatAtom } from "../state";
import { userAtom } from "../atoms/authAtom";
import { formattedPrice } from "../utils/price";
import OrderRow from "../components/OrderRow.js";
import Title from "../components/Title.js";
import AdminOrderStatus from "../admin/AdminOrderStatus.js";
import AdminOrderCancel from "../admin/AdminOrderCancel.js";

const fetchOrder = async ({ queryKey }) => {
  const [, orderId] = queryKey;
  const response = await fetch(`/userapi/orders/${orderId}`);
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }
  return response.json();
};

const mutateOrder = async (variables) => {
  const { orderId, data } = variables;
  const response = await fetch(`/api/orders/${orderId}/shipments`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }
  return response.json();
};

const fetchInventoryLogs = async ({ queryKey }) => {
  const [, orderId, admin] = queryKey;
  if (!admin) {
    return [];
  }

  const response = await fetch(`/api/orders/${orderId}/inventory_logs`);
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }
  return response.json();
};

const OrderDetails = ({ orderId, adminMode }) => {
  const queryClient = useQueryClient();

  const [countries] = useAtom(countriesAtom);
  const [currencies] = useAtom(currenciesAtom);
  const [showVat] = useAtom(showVatAtom);
  const [user] = useAtom(userAtom);

  const { t } = useTranslation();

  const {
    data: orderData,
    isLoading: orderIsLoading,
    isError: orderIsError,
  } = useQuery({
    queryKey: ["order", orderId],
    queryFn: fetchOrder,
  });

  const refreshOrderQueries = useCallback(
    () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ["order", orderId] }),
        queryClient.invalidateQueries({ queryKey: ["orderInventoryLogs", orderId] }),
        queryClient.invalidateQueries({ queryKey: ["adminOrders"] }),
        queryClient.invalidateQueries({ queryKey: ["orders"] }),
      ]),
    [orderId, queryClient],
  );

  const mutation = useMutation({
    mutationFn: mutateOrder,
    onSuccess: refreshOrderQueries,
  });

  const {
    data: inventoryLogsData,
    isLoading: logsIsLoading,
    isError: logsIsError,
  } = useQuery({
    queryKey: ["orderInventoryLogs", orderId, user?.admin],
    queryFn: fetchInventoryLogs,
  });

  const order = orderData?.order;

  const shipments = useMemo(() => orderData?.shipments || {}, [orderData?.shipments]);

  const [picked, setPicked] = useState({});

  const customer = order?.customer || {};
  const countryName = countries[order?.customer?.country_id]?.name || "";

  const total = useMemo(
    () =>
      (order?.order_rows || []).reduce((acc, item) => {
        const subtotal = showVat ? item.subtotal_incl_vat : item.subtotal_excl_vat;
        return acc + Number(subtotal);
      }, 0),
    [order?.order_rows, showVat],
  );

  const orderStatus = useMemo(() => {
    if (order?.shipped) {
      return {
        severity: "info",
        message: t("this_order_has_been_shipped") + ".",
        tracking: Object.values(shipments)[0]?.tracking_reference || null,
      };
    }

    if (order?.cancelled_at) {
      return {
        severity: "error",
        message: t("this_order_has_been_cancelled") + ".",
        tracking: null,
      };
    }

    return {
      severity: "warning",
      message: t("this_order_has_not_been_shipped") + ".",
      tracking: null,
    };
  }, [order?.cancelled_at, order?.shipped, shipments, t]);

  const sortedOrderRows = useMemo(() => {
    if (inventoryLogsData && inventoryLogsData.logs && order) {
      const updatedOrderRows = order.order_rows.map((row) => {
        const log = inventoryLogsData.logs.find((log) => log.product_id === row.product_id);
        const inventoryLocations = row.inventory_locations ? [...row.inventory_locations] : [];

        if (log) {
          inventoryLocations.push({
            name: log.inventory_location,
            change: log.change,
          });
        }

        return {
          ...row,
          inventory_locations: inventoryLocations,
        };
      });

      return updatedOrderRows.sort((a, b) => {
        const locationA = a.inventory_locations[0]?.name || "";
        const locationB = b.inventory_locations[0]?.name || "";
        return locationA.localeCompare(locationB);
      });
    }

    return order?.order_rows || [];
  }, [inventoryLogsData, order]);

  if (orderIsLoading || logsIsLoading) {
    return <Loader active inline="centered" />;
  }

  if (orderIsError) {
    return <Message error header="Error" content="Order could not be retrieved" />;
  }

  if (logsIsError) {
    return <Message error header="Error" content="Inventory logs could not be retrieved" />;
  }

  const pickCallback = (row) => {
    const newPicked = { ...picked };
    if (newPicked[row.id]) {
      newPicked[row.id] = newPicked[row.id] + 1;
    } else {
      newPicked[row.id] = 1;
    }
    setPicked(newPicked);
  };

  const unpickCallback = (row) => {
    const newPicked = { ...picked };
    if (newPicked[row.id]) {
      if (newPicked[row.id] === 1) {
        delete newPicked[row.id];
      } else {
        newPicked[row.id] = newPicked[row.id] - 1;
      }
    }
    setPicked(newPicked);
  };

  return (
    <>
      <Title
        title={`Order #${order?.id || ""}`}
        subtitle={order?.created_at ? new Date(order.created_at).toLocaleString() : ""}
      />

      {!order?.payment_slug && (
        <Grid stretched doubling stackable columns={adminMode ? 3 : 2} style={{ marginTop: "1em" }}>
          <Grid.Column width={adminMode ? 6 : 8}>
            <Message>
              <Message.Content>
                <Message.Header>
                  {customer.name}
                  {customer.company && (
                    <>
                      <br />
                      <small>{customer.company}</small>
                    </>
                  )}
                </Message.Header>
                <Divider />
                <Icon name="mail" />
                {customer.address1}
                <br />
                {customer.address2 && (
                  <>
                    {customer.address2}
                    <br />
                  </>
                )}
                {customer.postal_code} {customer.city}
                <br />
                {countryName}
                <br />
                <br />
                <Icon name="mail" />
                {customer.email}
                <br />
                <br />
                <Icon name="phone" />
                {customer.phone}
              </Message.Content>
            </Message>
          </Grid.Column>
          <Grid.Column width={adminMode ? 4 : 8}>
            <Message
              icon
              positive={orderStatus.severity === "info"}
              warning={orderStatus.severity === "warning"}
              error={orderStatus.severity === "error"}
            >
              <Icon name="shipping fast" />
              <Message.Content>
                <Message.Header>{t("order_status")}</Message.Header>
                <p>
                  <i>{order?.shipment_method?.product?.name}</i>
                </p>
                <p>{orderStatus.message}</p>
                {orderStatus.tracking && (
                  <p>
                    {t("tracking")}:{" "}
                    <strong>
                      <a
                        href={`${order.shipment_method?.tracking_url}${orderStatus.tracking}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {orderStatus.tracking}
                      </a>
                    </strong>
                  </p>
                )}
              </Message.Content>
            </Message>
          </Grid.Column>
          {adminMode && (
            <Grid.Column width={6}>
              <AdminOrderStatus
                order={order}
                shipments={shipments}
                mutation={mutation}
                picked={picked}
                onShipmentChanged={refreshOrderQueries}
              />
            </Grid.Column>
          )}
        </Grid>
      )}

      {order?.payment_slug && (
        <Message info>
          <Message.Header>{t("payment_link")}</Message.Header>
          <Message.Content>
            <a href={`/pay/${order.payment_slug}`} target="_blank" rel="noreferrer">
              {`${window.location.origin}/pay/${order.payment_slug}`}
            </a>
          </Message.Content>
        </Message>
      )}

      <Table celled stackable striped selectable>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>{t("product")}</Table.HeaderCell>
            <Table.HeaderCell>{t("price")}</Table.HeaderCell>
            <Table.HeaderCell>{t("quantity")}</Table.HeaderCell>
            <Table.HeaderCell>{t("subtotal")}</Table.HeaderCell>
            {user?.admin && (
              <>
                <Table.HeaderCell>{t("inventory_location")}</Table.HeaderCell>
                {!order?.shipped && <Table.HeaderCell>{t("stock_pick")}</Table.HeaderCell>}
                <Table.HeaderCell>{t("remaining_in_stock")}</Table.HeaderCell>
              </>
            )}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {sortedOrderRows.map((item, index) => (
            <OrderRow
              currency={order?.currency_id}
              item={item}
              key={index}
              pickCallback={pickCallback}
              unpickCallback={unpickCallback}
              picked={picked[item.id]}
              shipped={order.shipped}
            />
          ))}
        </Table.Body>
        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan={user?.admin ? "7" : "4"} textAlign="right">
              <Header as="h3">
                {formattedPrice(
                  total,
                  currencies[order?.currency_id]?.symbol,
                  currencies[order?.currency_id]?.right_side,
                )}
                <Header.Subheader>
                  <i>
                    {showVat ? `${t("including")} ${order?.country?.vat_percent}% ` : t("excluding")}
                    &nbsp;{t("vat")}
                  </i>
                </Header.Subheader>
              </Header>
              {adminMode && <AdminOrderCancel order={order} />}
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>
    </>
  );
};

export default React.memo(OrderDetails);
